import { useState } from 'react';
import { TextField } from './Form';
import { Spinner } from './Feedback';
import { useAsync, useDebounced } from '../lib/hooks';
import { useToast } from '../lib/toast';
import { errorMessage } from '../lib/api';
import { vendors } from '../lib/endpoints';

/**
 * SEARCH THE CACHED VENDOR LIST, OR CREATE ONE, INSTEAD OF TYPING A RAW QUICKBOOKS ID.
 *
 * A trading-partner connection binds to a real QuickBooks Vendor — required, not optional, the
 * moment a connection is requested as MyVendor (iERP AI's own validation refuses the request
 * otherwise, and Business Central's ToolKit enforces the same thing even more strictly at Accept).
 * Asking an operator to already know and type the numeric id is the wrong shape for "required": it
 * assumes the vendor exists and that its id is memorised, neither of which is true the first time
 * two companies connect. This is the same search-then-pick shape as AccountLookupField, plus the
 * one thing an account lookup never needs - creating the record on the spot when nothing matches.
 */
export function VendorLookupField({
  label = 'QuickBooks Vendor',
  hint,
  selectedId,
  selectedName,
  onChange,
  disabled,
}: {
  label?: string;
  hint?: string;
  selectedId: string | null;
  selectedName: string | null;
  onChange: (id: string | null, name: string | null) => void;
  disabled?: boolean;
}): JSX.Element {
  const toast = useToast();
  const [search, setSearch] = useState('');
  const debounced = useDebounced(search, 300);
  const term = debounced.trim();

  const results = useAsync(
    (signal) =>
      term ? vendors.list({ search: term, page: 1, pageSize: 20 }, signal) : Promise.resolve(null),
    [term],
  );

  const [creating, setCreating] = useState(false);

  const items = results.data?.items ?? [];
  const hasExactMatch = items.some((v) => v.displayName.trim().toLowerCase() === term.toLowerCase());

  const createVendor = async () => {
    setCreating(true);
    try {
      const created = await vendors.create({
        qboVendorId: null,
        displayName: term,
        companyName: null,
        email: null,
        phone: null,
        taxIdentifier: null,
        addressLine1: null,
        addressLine2: null,
        city: null,
        state: null,
        postalCode: null,
        country: null,
        termsId: null,
        currencyCode: null,
      });
      onChange(created.qboVendorId, created.displayName);
      setSearch('');
      toast.success('Vendor created', `${created.displayName} was created in QuickBooks.`);
    } catch (error) {
      toast.error('Vendor creation failed', errorMessage(error));
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="stack">
      {selectedId ? (
        <div className="field">
          <div className="field-label-row">
            <span className="field-label">{label}</span>
          </div>
          <div className="lookup-selected">
            <span className="cell-strong">{selectedName ?? 'Selected vendor'}</span>
            <span className="cell-sub mono">{selectedId}</span>
            {!disabled ? (
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => onChange(null, null)}>
                Change
              </button>
            ) : null}
          </div>
        </div>
      ) : (
        <>
          <TextField
            label={label}
            value={search}
            onChange={setSearch}
            placeholder="Search vendors by name"
            disabled={disabled}
            hint={hint}
          />
          {term ? (
            <div className="lookup-results">
              {results.loading ? (
                <Spinner label="Searching vendors" />
              ) : items.length === 0 ? (
                <p className="muted">No cached vendor matches "{term}".</p>
              ) : (
                <ul className="lookup-list">
                  {items.map((vendor) => (
                    <li key={vendor.qboVendorId}>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => onChange(vendor.qboVendorId, vendor.displayName)}
                      >
                        {vendor.displayName} <span className="cell-sub mono">{vendor.qboVendorId}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {!hasExactMatch ? (
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  disabled={creating}
                  onClick={() => void createVendor()}
                >
                  {creating ? 'Creating…' : `Create vendor "${term}"`}
                </button>
              ) : null}
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
